<script setup lang="ts">
import { onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import Badge from "@/components/shared/Badge.vue"
import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TableImage from "@/components/shared/TableImage.vue"
import TablePaginator from "@/components/shared/TablePaginator.vue"
import { getLocaleMessages } from "@/locales/index"
import type { SwatchStockListRequest } from "@/services/quality"
import qualityService from "@/services/quality"
import type { Quality } from "@/types/quality.type"
import { reportSentry } from "@/utils/sentry"
import { formattedDate } from "@/utils/string"

const route = useRoute()
const router = useRouter()

const items = ref<Quality[]>([])
const total = ref(0)

const query = ref<SwatchStockListRequest>({
  page: (route.query.page as string) || "1",
  perPage: (route.query.perPage as string) || "25",
  name: (route.query.name as string) || "",
  swatchStockStatus: (route.query.swatchStockStatus as string) || "",
  isSwatchOffered: (route.query.isSwatchOffered as string) || "",
})

// fetch function
async function loadItems() {
  try {
    const params = Object.entries(query.value).reduce((acc, [key, value]) => {
      acc[key as keyof SwatchStockListRequest] = value === "" ? null : value
      return acc
    }, {} as SwatchStockListRequest)

    const data = await qualityService.list({ ...params, status: "published" })
    items.value = data.items
    total.value = data.total
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

// initial load
onMounted(async () => {
  await loadItems()
})

// re-load whenever the query string changes
watch(
  () => route.query,
  (newQuery) => {
    query.value = {
      page: (newQuery.page as string) || "1",
      perPage: (newQuery.perPage as string) || "25",
      name: (newQuery.name as string) || "",
      swatchStockStatus: (newQuery.swatchStockStatus as string) || "",
      isSwatchOffered: (newQuery.isSwatchOffered as string) || "",
    }

    loadItems()
  },
)

// update URL (and trigger the watch) when the form submits
function searchItems() {
  query.value.page = "1"
  const params = Object.fromEntries(Object.entries(query.value).filter(([_, v]) => v !== ""))

  router.push({ path: route.path, query: params })
}
</script>

<template>
  <LayoutAuthed :title="$t('nav.swatchStocks')">
    <Card>
      <CardTitle>{{ $t("shared.search") }}</CardTitle>
      <form @submit.prevent="searchItems()">
        <CardContent>
          <div class="row">
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox v-model="query.name" :label="$t('item.name')" type="text" field="name" />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputSelect
                v-model="query.swatchStockStatus"
                :label="$t('item.swatchStockStatus')"
                field="swatchStockStatus"
                :options="[
                  { label: $t('item.swatchStockStatusList.inStock'), value: 'in_stock' },
                  { label: $t('item.swatchStockStatusList.outOfStock'), value: 'out_of_stock' },
                ]"
              />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputSelect
                v-model="query.isSwatchOffered"
                :label="$t('item.isSwatchOffered')"
                field="isSwatchOffered"
                :options="
                  Object.entries(getLocaleMessages().item.isSwatchOfferedList).map(([v, k]) => ({ label: k, value: v }))
                "
              />
            </div>
          </div>
        </CardContent>
        <CardActions>
          <Button type="submit" :text="$t('shared.search')" />
        </CardActions>
      </form>
    </Card>
    <Card>
      <CardTitle>
        <template #default> {{ $t("shared.results") }} ({{ total }}) </template>
      </CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("item.img") }}</th>
              <th>{{ $t("item.name") }}</th>
              <th>{{ $t("item.swatchStock") }}</th>
              <th>{{ $t("item.swatchStockUpdatedAt") }}</th>
              <th>{{ $t("item.isSwatchOffered") }}</th>
              <th></th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(item, index) in items" :key="index">
              <td>
                <TableImage v-if="item.storeSwatchImage" :image="item.storeSwatchImage" />
                <span v-else>-</span>
              </td>
              <td>
                <router-link :to="`/items/${item.id}`">{{ item.name }}</router-link>
              </td>
              <td>
                {{ item.stock }}
                <Badge v-if="item.pendingStock > 0" :text="$t('item.swatchStockInProgress')" :style="'info'" />
              </td>
              <td>{{ formattedDate(item.stockUpdatedAt) }}</td>
              <td>
                <StatusBadge model="item" field="isSwatchOffered" :value="String(item.isSwatchOffered)" />
              </td>
              <td>
                <router-link :to="`/swatch_stocks/${item.id}/edit`">
                  {{ $t("shared.edit") }}
                </router-link>
              </td>
            </tr>
          </template>
        </Table>
        <TablePaginator
          :total="total"
          :perPage="Number(query.perPage)"
          :page="Number(query.page)"
          :url="route.fullPath"
        />
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped></style>
